import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, ReportIdSchema, ScanIdSchema } from "../primitives/ids.js";
import { ExternalReadinessCategorySchema, ExternalReadinessPrioritySchema, ExternalReadinessSeveritySchema, } from "./external-readiness-report.js";
import { EXTERNAL_AFTER_FIX_PROOF_RULE_SET_VERSION } from "./report-lifecycle.js";
export const ExternalAfterFixProofIdSchema = z
    .string()
    .regex(/^afproof_ext_[a-zA-Z0-9_-]+$/)
    .brand();
export const ExternalAfterFixProofLifecycleStatusSchema = z.enum([
    "AFTER_FIX_RESCAN_DRAFT",
    "SCAN_PENDING",
    "SCAN_RUNNING",
    "READY_INTERNAL",
    "READY_CLIENT_SAFE_SUMMARY",
    "FAILED",
    "SUPERSEDED",
]);
export const ExternalAfterFixComparisonStatusSchema = z.enum([
    "FIXED",
    "STILL_OPEN",
    "NEEDS_PROOF",
    "NOT_CHECKED",
    "CHANGED_BUT_UNVERIFIED",
]);
export const ExternalAfterFixProofContextV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.context.v1"),
    afterFixProofId: ExternalAfterFixProofIdSchema,
    priorScanId: ScanIdSchema,
    priorSnapshotId: z.string().min(1),
})
    .strict();
export const ExternalAfterFixFindingComparedV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.findingCompared.v1"),
    findingId: z.string().min(1),
    sourceRef: z.string().min(1).max(2048),
    category: ExternalReadinessCategorySchema,
    severity: ExternalReadinessSeveritySchema,
    priority: ExternalReadinessPrioritySchema,
    title: z.string().min(1).max(200),
    status: ExternalAfterFixComparisonStatusSchema,
    evidenceNote: z.string().min(1).max(1000),
    priorCheckStatus: z.string().min(1).optional(),
    newCheckStatus: z.string().min(1).optional(),
    checkId: z.string().min(1).optional(),
    needsProof: z.boolean(),
})
    .strict();
export const ExternalAfterFixActionItemComparedV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.actionItemCompared.v1"),
    actionItemId: z.string().min(1),
    sourceFindingId: z.string().min(1),
    sourceRef: z.string().min(1).max(2048),
    category: ExternalReadinessCategorySchema,
    severity: ExternalReadinessSeveritySchema,
    priority: ExternalReadinessPrioritySchema,
    issueSummary: z.string().min(1).max(500),
    status: ExternalAfterFixComparisonStatusSchema,
    evidenceNote: z.string().min(1).max(1000),
    priorCheckStatus: z.string().min(1).optional(),
    newCheckStatus: z.string().min(1).optional(),
    checkId: z.string().min(1).optional(),
    needsProof: z.boolean(),
})
    .strict();
export const ExternalAfterFixProofSourceRefsV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.sourceRefs.v1"),
    externalReadinessReportId: ReportIdSchema,
    externalProofFixKitId: z.string().min(1),
    externalProofSalesRoomId: z.string().min(1).optional(),
    priorScanId: ScanIdSchema,
    priorSnapshotId: z.string().min(1),
    newScanId: ScanIdSchema.optional(),
    newSnapshotId: z.string().min(1).optional(),
    attachedUrl: z.string().url().optional(),
})
    .strict();
/** Canonical internal storage document for Phase 7 after-fix re-scan proof. */
export const ExternalAfterFixProofV1Schema = z
    .object({
    schemaVersion: z.literal("external-after-fix-proof.v1.0"),
    afterFixProofId: ExternalAfterFixProofIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    sourceRefs: ExternalAfterFixProofSourceRefsV1Schema,
    lifecycleStatus: ExternalAfterFixProofLifecycleStatusSchema,
    ruleSetVersion: z.literal(EXTERNAL_AFTER_FIX_PROOF_RULE_SET_VERSION),
    comparisonRuleSetVersion: z.literal(EXTERNAL_AFTER_FIX_PROOF_RULE_SET_VERSION),
    findingsCompared: z.array(ExternalAfterFixFindingComparedV1Schema),
    actionItemsCompared: z.array(ExternalAfterFixActionItemComparedV1Schema),
    evidenceRefs: z.array(z.string().min(1).max(500)),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    comparedAt: z.string().datetime().optional(),
    generatedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ExternalAfterFixProofInternalViewV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.internalView.v1"),
    afterFixProofId: ExternalAfterFixProofIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    lifecycleStatus: ExternalAfterFixProofLifecycleStatusSchema,
    sourceRefs: ExternalAfterFixProofSourceRefsV1Schema,
    findingsCompared: z.array(ExternalAfterFixFindingComparedV1Schema),
    actionItemsCompared: z.array(ExternalAfterFixActionItemComparedV1Schema),
    summaryCounts: z.object({
        fixed: z.number().int().nonnegative(),
        stillOpen: z.number().int().nonnegative(),
        needsProof: z.number().int().nonnegative(),
        notChecked: z.number().int().nonnegative(),
        changedButUnverified: z.number().int().nonnegative(),
    }),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    comparedAt: z.string().datetime().optional(),
    generatedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    clientSafe: z.literal(false),
})
    .strict();
export const ExternalAfterFixProofClientSafeItemV1Schema = z
    .object({
    label: z.string().min(1),
    status: ExternalAfterFixComparisonStatusSchema,
    summary: z.string().min(1),
})
    .strict();
export const ExternalAfterFixProofClientSafeSummaryV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.clientSafeSummary.v1"),
    afterFixProofId: ExternalAfterFixProofIdSchema,
    projectId: ProjectIdSchema,
    lifecycleStatus: z.literal("READY_CLIENT_SAFE_SUMMARY"),
    headline: z.string().min(1),
    summaryCounts: z.object({
        fixed: z.number().int().nonnegative(),
        stillOpen: z.number().int().nonnegative(),
        needsProof: z.number().int().nonnegative(),
    }),
    items: z.array(ExternalAfterFixProofClientSafeItemV1Schema),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    comparedAt: z.string().datetime().optional(),
    generatedAt: z.string().datetime(),
    clientSafe: z.literal(true),
})
    .strict();
export const StartExternalAfterFixRescanRequestV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.startRescanRequest.v1"),
    forceRegenerate: z.boolean().optional(),
})
    .strict();
export const StartExternalAfterFixRescanResultV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.startRescanResult.v1"),
    afterFixProofId: ExternalAfterFixProofIdSchema,
    lifecycleStatus: ExternalAfterFixProofLifecycleStatusSchema,
    newScanId: ScanIdSchema,
    newSnapshotId: z.string().min(1),
    priorScanId: ScanIdSchema,
    priorSnapshotId: z.string().min(1),
    startedAt: z.string().datetime(),
})
    .strict();
export const ExternalAfterFixProofStatusV1Schema = z
    .object({
    schemaId: z.literal("externalAfterFixProof.status.v1"),
    afterFixProofId: ExternalAfterFixProofIdSchema,
    lifecycleStatus: ExternalAfterFixProofLifecycleStatusSchema,
    newScanStatus: z.enum(["PENDING", "PROCESSING", "COMPLETE", "FAILED"]).optional(),
    failureReason: z.string().min(1).optional(),
    comparedAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=external-after-fix-proof.js.map